// frontend/src/components/players/PDFViewer.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSecureMedia } from '../../services/mediaService';
import * as pdfjsLib from 'pdfjs-dist';

// ✅ استفاده از فایل worker محلی با پسوند .js (سازگار با Nginx)
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// تنظیمات بهینه برای سرعت بالا و پشتیبانی از PDFهای تصویری
const PDF_JS_OPTIONS = {
  disableAutoFetch: true,
  disableStream: false,
  useWorkerFetch: true,
  useSystemFonts: true,
  cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.296/cmaps/',
  cMapPacked: true,
  standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.296/standard_fonts/',
  maxImageSize: 1024 * 1024, // ✅ افزایش برای کیفیت بهتر تصاویر
  enableXfa: false,
  stopAtErrors: false,
  fontExtraProperties: false,
  isEvalSupported: false,
  useStandardFontData: true,
  verbosity: 0,
};

// تبدیل اعداد به فارسی (فقط برای نمایش در نوارها)
const toPersianNumber = (num) => {
  const persianDigits = '۰۱۲۳۴۵۶۷۸۹';
  return String(num).replace(/\d/g, (d) => persianDigits[parseInt(d)]);
};

const PDFViewer = ({ mediaId, fileUrl, title }) => {
  const { mediaUrl, isLoading: urlLoading, error: urlError, refetch: refetchMediaUrl } =
    useSecureMedia(mediaId);

  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [loading, setLoading] = useState(true);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [renderProgress, setRenderProgress] = useState(0);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const loadingTaskRef = useRef(null);
  const renderTaskRef = useRef(null);
  const isMounted = useRef(true);

  const directPdf = fileUrl || mediaUrl;
  const proxyUrl = directPdf?.startsWith('https://s3.afra.kubit.cloud/')
    ? `https://farzaneh.app/api/proxy/media/?url=${encodeURIComponent(directPdf)}`
    : directPdf;

  // اندازه‌گیری عرض کانتینر
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth;
        setContainerWidth(width - 40 || 800);
      } else {
        setContainerWidth(800);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // بارگذاری سریع PDF
  useEffect(() => {
    if (!proxyUrl) return;

    const loadPDF = async () => {
      try {
        setLoading(true);
        setRenderProgress(0);
        
        if (loadingTaskRef.current) {
          loadingTaskRef.current.destroy();
        }
        if (renderTaskRef.current) {
          await renderTaskRef.current.promise.then(() => {}, () => {});
        }

        const loadingTask = pdfjsLib.getDocument({
          url: proxyUrl,
          ...PDF_JS_OPTIONS,
          withCredentials: false,
          rangeChunkSize: 64 * 1024,
        });

        loadingTaskRef.current = loadingTask;

        loadingTask.onProgress = (progress) => {
          if (isMounted.current) {
            const percent = Math.round((progress.loaded / progress.total) * 100);
            setRenderProgress(Math.min(percent, 99));
          }
        };

        const pdf = await loadingTask.promise;
        
        if (isMounted.current) {
          setPdfDoc(pdf);
          setNumPages(pdf.numPages);
          setPageNumber(1);
          setRenderProgress(100);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error loading PDF:', error);
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    loadPDF();

    return () => {
      isMounted.current = false;
      if (loadingTaskRef.current) {
        loadingTaskRef.current.destroy();
      }
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
      if (pdfDoc) {
        pdfDoc.destroy();
      }
    };
  }, [proxyUrl]);

  // رندر صفحه با کیفیت بالا برای PDFهای تصویری
  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current) return;

    try {
      if (renderTaskRef.current) {
        await renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }

      const page = await pdfDoc.getPage(pageNumber);
      
      const viewport = page.getViewport({ scale: 1 });
      
      const containerWidth_ = containerWidth || 800;
      const containerHeight = window.innerHeight - 160;
      
      const scaleX = containerWidth_ / viewport.width;
      const scaleY = containerHeight / viewport.height;
      
      const finalScale = Math.min(Math.max(Math.min(scaleX, scaleY), 0.5), 2.0);
      
      // ✅ افزایش کیفیت برای PDFهای تصویری
      const devicePixelRatio = Math.min(window.devicePixelRatio || 1, 2.0);
      const renderScale = finalScale * devicePixelRatio;

      const scaledViewport = page.getViewport({ scale: renderScale });

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d', { 
        alpha: false,
        willReadFrequently: false,
      });

      // ✅ تنظیم کیفیت بالا برای تصاویر
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';

      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      
      const displayWidth = scaledViewport.width / devicePixelRatio;
      const displayHeight = scaledViewport.height / devicePixelRatio;
      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${displayHeight}px`;

      // ✅ تنظیمات مخصوص PDFهای تصویری
      const renderContext = {
        canvasContext: context,
        viewport: scaledViewport,
        background: '#fff',
        enableWebGL: false,
        renderInteractiveForms: false,
        enableXfa: false,
        intent: 'display', // ← مهم برای نمایش تصاویر
        annotationMode: 0,
        useSystemFonts: true,
        fontExtraProperties: true,
        // ✅ برای کیفیت بهتر تصاویر
        imageLayer: true,
      };

      const renderTask = page.render(renderContext);
      renderTaskRef.current = renderTask;
      
      await renderTask.promise;
      renderTaskRef.current = null;
      
      setScale(finalScale);
    } catch (error) {
      if (error.name !== 'RenderingCancelledException') {
        console.error('Render error:', error);
      }
    }
  }, [pdfDoc, pageNumber, containerWidth]);

  useEffect(() => {
    if (pdfDoc && !loading) {
      renderPage();
    }
  }, [renderPage, pdfDoc, loading]);

  const changePage = useCallback((offset) => {
    setPageNumber(prev => {
      const newPage = prev + offset;
      return Math.max(1, Math.min(newPage, numPages));
    });
  }, [numPages]);

  const goToPage = (page) => {
    const newPage = Math.max(1, Math.min(page, numPages));
    setPageNumber(newPage);
  };

  const zoomIn = () => setScale(prev => Math.min(prev + 0.2, 2.5));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));
  const zoomFit = () => {
    if (containerRef.current && pdfDoc) {
      renderPage();
    }
  };

  // جلوگیری از دانلود
  const handleContextMenu = (e) => { e.preventDefault(); };
  const handleDragStart = (e) => { e.preventDefault(); };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'p')) {
      e.preventDefault();
    }
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      changePage(1);
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      changePage(-1);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [changePage]);

  // وضعیت خطا
  if (urlError) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center">
        <p className="font-bold text-red-400 mb-4">{urlError}</p>
        <button onClick={() => refetchMediaUrl?.()} className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg">
          تلاش مجدد
        </button>
      </div>
    );
  }

  // نمایش پیشرفت بارگذاری
  if (urlLoading || loading || !proxyUrl) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center">
        <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-600 mb-2">در حال بارگذاری...</p>
        <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-teal-500 transition-all duration-300"
            style={{ width: `${renderProgress}%` }}
          />
        </div>
        <p className="text-xs text-gray-400 mt-2">{renderProgress}%</p>
      </div>
    );
  }

  return (
    <div 
      className="h-screen flex flex-col bg-gray-50 select-none"
      onContextMenu={handleContextMenu}
      onDragStart={handleDragStart}
      dir="ltr"
    >
      {/* نوار واحد: هدر + کنترل‌ها + شماره صفحه با پس‌زمینه teal-800 */}
      <div className="bg-teal-800 text-white px-4 py-3 shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-2">
          {/* عنوان (حذف شده) */}
          
          {/* بخش چپ: کنترل‌های زوم */}
          <div className="flex items-center gap-1">
            <button 
              onClick={zoomOut} 
              className="p-2 rounded hover:bg-teal-700 transition-colors"
              title="کم کردن بزرگنمایی"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1v-1z" />
              </svg>
            </button>
            <span className="text-xs min-w-[45px] text-center" style={{ fontFamily: 'Vazirmatn, sans-serif' }}>
              {toPersianNumber(Math.round(scale * 100))}%
            </span>
            <button 
              onClick={zoomIn} 
              className="p-2 rounded hover:bg-teal-700 transition-colors"
              title="افزایش بزرگنمایی"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 10v6m-3-3h6" />
              </svg>
            </button>
            <button 
              onClick={zoomFit} 
              className="px-2 py-1 text-xs rounded hover:bg-teal-700 transition-colors"
              style={{ fontFamily: 'Vazirmatn, sans-serif' }}
            >
              Fit
            </button>
          </div>

          {/* بخش وسط: کنترل‌های صفحه */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => changePage(-1)}
              disabled={pageNumber <= 1}
              className="p-1.5 rounded hover:bg-teal-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <input
              type="number"
              min={1}
              max={numPages}
              value={pageNumber}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                if (val >= 1 && val <= numPages) goToPage(val);
              }}
              className="w-12 px-1 py-1 bg-white/20 text-white text-center rounded border border-white/30 focus:outline-none focus:border-white text-sm"
              style={{ fontFamily: 'Vazirmatn, sans-serif' }}
            />
            <span className="text-xs opacity-75" style={{ fontFamily: 'Vazirmatn, sans-serif' }}>
              / {toPersianNumber(numPages)}
            </span>
            <button
              onClick={() => changePage(1)}
              disabled={pageNumber >= numPages}
              className="p-1.5 rounded hover:bg-teal-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* بخش راست: دکمه تمام صفحه */}
          <button
            onClick={() => {
              const elem = document.querySelector('.pdf-viewer-container');
              if (elem?.requestFullscreen) elem.requestFullscreen();
            }}
            className="p-1.5 rounded hover:bg-teal-700 transition-colors"
            title="تمام صفحه"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
            </svg>
          </button>
        </div>
      </div>

      {/* نمایش PDF */}
      <div 
        ref={containerRef}
        className="flex-1 flex items-center justify-center bg-gray-50 p-2 sm:p-4 overflow-auto"
      >
        <div className="relative shadow-xl bg-white rounded-lg p-2">
          <canvas ref={canvasRef} className="bg-white" />
          {/* آبی‌نگار */}
          <div className="absolute inset-0 pointer-events-none opacity-5 flex items-center justify-center">
            <span className="text-4xl font-bold text-gray-500 rotate-[-30deg]">
              FARZANEH
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;